import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Info, SearchX } from "lucide-react";

import { Card } from "@/components/ui/Card";
import DataTable from "@/components/shared/DataTable";
import { TableSkeleton } from "@/components/shared/TableSkeleton";
import { Tag } from "@/components/shared/Tag";
import StatusBadge from "@/components/shared/StatusBadge";
import { formatDate } from "@/utils/dateUtils";

const degradedName = (entry) => {
  if (entry == null) return "One organization";
  if (typeof entry === "string") return entry;
  return entry.name || entry.tenantName || entry.slug || entry.id || "One organization";
};

/**
 * The search results. Columns are ordered so two people with the same name are
 * distinguishable: masked email, masked phone, application count, latest job,
 * created date, why the row matched — and the tenant, on the SuperAdmin screen
 * only. `matchedOn` is why the row is in the list and `deleted` is never
 * hidden: a complaint usually follows a rejection and a delete.
 */
const ResultsTable = ({ results = [], loading = false, degraded = [], truncated = false, isSuperAdmin = false, onSelectRow, query = "" }) => {
  // `results` is already in hand — the search fetched it. DataTable pages and
  // renders a list passed as `apiData`, and takes the page state as props, so
  // 200 matches arrive one page at a time rather than all at once.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // A new search is a new list. Page 3 of the old one is not a page of it.
  useEffect(() => {
    setCurrentPage(1);
  }, [results]);

  const columns = useMemo(() => {
    const cols = [
      {
        key: "displayName",
        label: "Name",
        sortable: false,
        render: (val) => <span className="font-medium text-[13px] text-text-primary">{val ?? "—"}</span>,
      },
      {
        key: "email",
        label: "Email",
        sortable: false,
        render: (val) => (
          <span className="text-text-secondary" title={val ?? ""}>
            {val ?? "—"}
          </span>
        ),
      },
      {
        key: "phone",
        label: "Phone",
        sortable: false,
        render: (val) => <span className="text-text-secondary">{val ?? "—"}</span>,
      },
      {
        key: "applicationCount",
        label: "Applications",
        sortable: false,
        align: "center",
        render: (val) => <span>{val ?? 0}</span>,
      },
      {
        key: "latestJobTitle",
        label: "Most Recent Job",
        sortable: false,
        render: (val) => (
          <span className="block max-w-[220px] truncate" title={val ?? ""}>
            {val ?? "—"}
          </span>
        ),
      },
      {
        key: "createdAt",
        label: "Created",
        sortable: false,
        render: (val) => <span>{formatDate(val)}</span>,
      },
      {
        key: "matchedOn",
        label: "Matched On",
        sortable: false,
        render: (val) => (val ? <Tag text={val} color="blue" /> : <span className="text-text-muted">—</span>),
      },
      {
        key: "deleted",
        label: "Deleted",
        sortable: false,
        align: "center",
        render: (val) => <StatusBadge isActive={!val} isDeleted={Boolean(val)} size="sm" />,
      },
    ];

    if (isSuperAdmin) {
      cols.splice(
        cols.findIndex((c) => c.key === "applicationCount"),
        0,
        {
          key: "tenantName",
          label: "Tenant",
          sortable: false,
          render: (val, row) => (
            <span className="block max-w-[180px] truncate text-text-secondary" title={val ?? row?.tenantId ?? ""}>
              {val ?? "—"}
            </span>
          ),
        },
      );
    }
    return cols;
  }, [isSuperAdmin]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border-primary bg-background-secondary">
        <TableSkeleton rows={6} />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-3">
      {degraded.length > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-tone-amber/30 bg-tone-amber-soft px-3.5 py-2.5">
          <AlertTriangle size={15} className="mt-0.5 shrink-0 text-tone-amber" aria-hidden="true" />
          <p className="text-[12px] leading-5 text-text-primary">
            {degraded.map(degradedName).join(", ")}{" "}
            {degraded.length > 1
              ? "could not be reached — results from those organizations are missing."
              : "could not be reached — results from that organization are missing."}
          </p>
        </div>
      )}

      {/* The table's own "No data available" would sit above the sentence
          below, so the empty case replaces the table rather than joining it. */}
      {results.length === 0 ? (
        <Card className="flex w-full flex-col items-center gap-2 rounded-xl border border-border-primary bg-background-secondary px-6 py-12 text-center">
          <SearchX size={20} className="text-text-muted" aria-hidden="true" />

          <p className="text-[13px] font-medium text-text-primary">No matches for “{query}”</p>

          <p className="max-w-md text-[12px] leading-5 text-text-secondary">Try a full name, an email address, a phone number, or an id.</p>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border-primary bg-background-secondary">
          <DataTable
            apiData={results}
            columns={columns}
            rowKey="subjectId"
            sortable={false}
            showPagination
            paginationLabel="matches"
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            onRowClick={onSelectRow}
          />
        </div>
      )}

      {truncated && (
        <div className="flex items-center gap-2 text-[12px] text-text-secondary">
          <Info size={14} className="shrink-0" aria-hidden="true" />
          Showing the first 200 matches — narrow your search.
        </div>
      )}
    </div>
  );
};

export default ResultsTable;
